import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePartnerContext } from '../../context'
import SEO from '../seo'
import Button from '../tailwind/Button'
import VersionSwitch from '../version-switch'
import SettingsV1 from './result-reports/settings-v1'
import SettingsV2 from './result-reports/settings-v2'

const TRANSLATION_LOCATION = 'components.settings.result_reports.'

const Header = ({ versions = [], withVersionSwitch = false }) => {
  const { t } = useTranslation()

  return (
    <>
      <h1 className='text-xl font-semibold text-gray-900'>
        {t(TRANSLATION_LOCATION + 'title')}
      </h1>
      <p className='mt-1 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
        {t(TRANSLATION_LOCATION + 'description')}
      </p>
      {withVersionSwitch && (
        <div className='relative mt-4'>
          <div
            aria-hidden='true'
            className='absolute inset-0 flex items-center'
          >
            <div className='w-full border-t border-gray-300' />
          </div>
          <div className='relative flex justify-center'>
            <div className='bg-white px-2'>
              <VersionSwitch versions={versions} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

Header.propTypes = {
  versions: PropTypes.array,
  withVersionSwitch: PropTypes.bool
}

const Container = ({ children = null }) => {
  return (
    <div className='bg-gray-50 px-4 py-5 sm:rounded-md sm:p-6'>{children}</div>
  )
}

Container.propTypes = { children: PropTypes.node }

const ResultReports = ({ careers }) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()

  const hasV1Settings = !!Object.keys(
    JSON.parse(partner.app_settings?.report_v1_settings ?? '{}')
  ).length

  const hasV2Settings = !!Object.keys(
    JSON.parse(partner.app_settings?.report_v2_settings ?? '{}')
  ).length

  const hasV1Careers = careers.some(
    ({ diagnostic_version }) => diagnostic_version === 1
  )

  const hasV2Careers = careers.some(
    ({ diagnostic_version }) => diagnostic_version === 2
  )

  const [activeVersion, setActiveVersion] = useState(() => {
    if (hasV2Careers && !hasV1Careers) return 2
    if (!hasV2Careers && hasV1Careers) return 1
    if (hasV1Careers && hasV1Settings) return 1

    return 2
  })

  const [displayV1Settings, setDisplayV1Settings] = useState(hasV1Settings)
  const [displayV2Settings, setDisplayV2Settings] = useState(hasV2Settings)

  const versions = [
    {
      version: 1,
      active: activeVersion === 1,
      handleChange: () => setActiveVersion(1)
    },
    {
      version: 2,
      active: activeVersion === 2,
      handleChange: () => setActiveVersion(2)
    }
  ]

  return (
    <>
      <SEO title={t('seo.titles.settings_resultreports')} />
      <Header
        {...{ versions, withVersionSwitch: hasV1Careers && hasV2Careers }}
      />
      <div className='mt-4' />
      {activeVersion === 1 && (
        <>
          {displayV1Settings ? (
            <Container>
              <SettingsV1 />
            </Container>
          ) : (
            <Button.PrimaryLG
              text={t(TRANSLATION_LOCATION + 'lockedScreen.button')}
              onClick={() => setDisplayV1Settings(true)}
            />
          )}
        </>
      )}
      {activeVersion === 2 && (
        <>
          {displayV2Settings ? (
            <SettingsV2 />
          ) : (
            <Button.PrimaryLG
              text={t(TRANSLATION_LOCATION + 'lockedScreen.button')}
              onClick={() => setDisplayV2Settings(true)}
            />
          )}
        </>
      )}
    </>
  )
}

ResultReports.propTypes = {
  careers: PropTypes.array.isRequired
}

export default ResultReports
